/**
 * Shared pointer + raycast plumbing for click-to-pick game UIs.
 *
 * Both mahjong and dominoes do the same dance: pointerdown captures the
 * pointer, pointerup checks the move-distance threshold (so a drag doesn't
 * fire a click), then a raycast against a list of candidate meshes; from
 * the hit object, walk up to the topmost ancestor that owns a logical id
 * (Group + child meshes share the same userData key).
 *
 * `createPointerPicker` handles the pointerdown/up/move + NDC math + drag
 * threshold, and exposes the live `Raycaster` to the callbacks. Consumers
 * raycast against whatever object lists they care about, and use
 * `findUserDataAncestor` to resolve the hit to its logical group.
 */

import { Camera, Object3D, Raycaster, Vector2 } from 'three';

export interface PointerPickerOptions {
  canvas: HTMLCanvasElement;
  camera: Camera;
  /** Fired on a clean tap (pointer didn't drag past `moveThresholdPx`). */
  onTap?: (event: PointerEvent, raycaster: Raycaster) => void;
  /** Fired on every pointermove. Skipped if undefined (no listener attached). */
  onHover?: (event: PointerEvent, raycaster: Raycaster) => void;
  /** Pixels of pointer movement during a press above which a tap becomes a
   *  drag and `onTap` is suppressed. Default 6. */
  moveThresholdPx?: number;
}

export interface PointerPicker {
  dispose(): void;
}

export function createPointerPicker(opts: PointerPickerOptions): PointerPicker {
  const { canvas, camera, onTap, onHover, moveThresholdPx = 6 } = opts;
  const ray = new Raycaster();
  const ndc = new Vector2();
  let pointerDown: { x: number; y: number; id: number } | null = null;

  function setRayFromEvent(e: PointerEvent) {
    const rect = canvas.getBoundingClientRect();
    ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    ray.setFromCamera(ndc, camera);
  }

  const onDown = (e: PointerEvent) => {
    pointerDown = { x: e.clientX, y: e.clientY, id: e.pointerId };
    canvas.setPointerCapture?.(e.pointerId);
  };
  const onUp = (e: PointerEvent) => {
    const start = pointerDown;
    pointerDown = null;
    canvas.releasePointerCapture?.(e.pointerId);
    if (!start || start.id !== e.pointerId) return;
    if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > moveThresholdPx) return;
    if (!onTap) return;
    setRayFromEvent(e);
    onTap(e, ray);
  };
  const onMove = (e: PointerEvent) => {
    if (!onHover) return;
    setRayFromEvent(e);
    onHover(e, ray);
  };

  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointerup', onUp);
  if (onHover) canvas.addEventListener('pointermove', onMove);

  return {
    dispose() {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointermove', onMove);
    },
  };
}

/**
 * Walk up from a raycast hit to the topmost ancestor that owns a value at
 * `userData[key]`. Used to collapse Group + child Mesh hierarchies that all
 * stamp the same logical id (e.g. a tile group whose body + face meshes
 * share `userData.tileId`).
 */
export function findUserDataAncestor(hit: Object3D, key: string): Object3D | null {
  let cur: Object3D | null = hit;
  while (cur && cur.userData[key] === undefined) cur = cur.parent;
  if (!cur) return null;
  while (cur.parent && cur.parent.userData[key] === cur.userData[key]) {
    cur = cur.parent;
  }
  return cur;
}
